/**
 * Route-aware BOQ version prefixing.
 *
 * /roads-sewers and /groundworks keep independent BOQ version sequences for the
 * same project, keyed as RS-1, RS-2... and GWKS-1, GWKS-2... respectively — both
 * in the boq-versions URL path segment and the request/response BODY `version`
 * field. Outside those two routes (dashboard, version history, generic hooks)
 * versions are left exactly as provided so existing behaviour there is unaffected.
 */

export type BoqVersionRoutePrefix = "RS" | "GWKS";

const RS_ROUTE_SEGMENT = "/roads-sewers";
const GWKS_ROUTE_SEGMENT = "/groundworks";

const VERSION_PREFIX_PATTERN = /^(RS|GWKS)-(\d+)$/i;

/** Detects which job-type workflow route is currently active, if any. */
export function getBoqVersionRoutePrefix(): BoqVersionRoutePrefix | null {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname;
  if (path.includes(RS_ROUTE_SEGMENT)) return "RS";
  if (path.includes(GWKS_ROUTE_SEGMENT)) return "GWKS";
  return null;
}

/**
 * Strips a known RS-/GWKS- prefix (if present) and returns the plain numeric
 * version. Accepts number or string so callers don't need to special-case either.
 */
export function getNumericBoqVersion(version: number | string): number {
  if (typeof version === "number") return version;
  const match = VERSION_PREFIX_PATTERN.exec(version.trim());
  const raw = match ? match[2] : version;
  const parsed = parseInt(String(raw), 10);
  return Number.isNaN(parsed) ? 1 : parsed;
}

/**
 * Extracts the RS/GWKS prefix from a version value already carrying one (e.g.
 * "GWKS-3" -> "GWKS"), or null for a plain/legacy numeric version. Use this on
 * pages that aren't nested under /roads-sewers or /groundworks (so route-based
 * detection can't apply) but that already have a job-type-specific version
 * value in hand — e.g. /version-history, where each row is independently
 * RS-N or GWKS-N and the page itself has no route to detect from.
 */
export function getBoqVersionPrefixFromValue(
  version: number | string | undefined,
): BoqVersionRoutePrefix | null {
  if (version === undefined || typeof version === "number") return null;
  const match = VERSION_PREFIX_PATTERN.exec(version.trim());
  return match ? (match[1].toUpperCase() as BoqVersionRoutePrefix) : null;
}

/**
 * Builds the version key to use on the wire (URL path segment + request/response
 * BODY `version` field) for a boq-versions API call. When the current route is
 * /roads-sewers or /groundworks, the numeric part of `version` is re-prefixed to
 * match that route (RS-N / GWKS-N), regardless of whether `version` already
 * carries a prefix. Outside those routes, `version` is returned unchanged —
 * unless `prefixOverride` is passed (e.g. from a page that isn't nested under
 * either route, like /tender/import-takeoff, but already knows which job
 * type's version it's writing), in which case that prefix is used instead of
 * (and without even consulting) route-based detection.
 */
export function toBoqVersionKey(
  version: number | string,
  prefixOverride?: BoqVersionRoutePrefix | null,
): number | string {
  const prefix = prefixOverride !== undefined ? prefixOverride : getBoqVersionRoutePrefix();
  if (!prefix) return version;
  return `${prefix}-${getNumericBoqVersion(version)}`;
}

/**
 * Highest numeric version among entries belonging to the active route's
 * sequence (RS-N / GWKS-N), or across all entries outside those routes.
 * Centralises the scoping so every "latest version" computation stays in
 * sync — computing Math.max directly over raw `v.version` strings (e.g.
 * "GWKS-3") coerces to NaN instead of parsing the numeric part.
 */
export function getScopedLatestVersionNumber(
  versions: Array<{ version?: number | string }> | undefined,
  prefixOverride?: BoqVersionRoutePrefix | null,
): number | undefined {
  if (!versions || versions.length === 0) return undefined;
  const prefix = prefixOverride !== undefined ? prefixOverride : getBoqVersionRoutePrefix();
  const scoped = prefix
    ? versions.filter((v) => String(v.version ?? "").toUpperCase().startsWith(`${prefix}-`))
    : versions;
  if (scoped.length === 0) return undefined;
  return Math.max(...scoped.map((v) => getNumericBoqVersion(v.version ?? 1)));
}

/**
 * Same as getScopedLatestVersionNumber, but also handles the boq-versions API
 * quirk where `versions` comes back as [] when the project has exactly one
 * version (that lone version isn't listed as "history"). In that case, falls
 * back to the response's top-level `currentVersion` — but only when it
 * actually belongs to the active route's sequence, so a lone RS-1 doesn't get
 * mistaken for the current GWKS version (and vice versa) when viewed from the
 * other workflow.
 */
export function getScopedCurrentVersionNumber(
  versionHistory: {
    currentVersion?: number | string;
    totalVersions?: number;
    versions?: Array<{ version?: number | string }>;
  },
  prefixOverride?: BoqVersionRoutePrefix | null,
): number | undefined {
  const fromList = getScopedLatestVersionNumber(versionHistory.versions, prefixOverride);
  if (fromList !== undefined) return fromList;

  if (!versionHistory.totalVersions || versionHistory.currentVersion === undefined) {
    return undefined;
  }
  const prefix = prefixOverride !== undefined ? prefixOverride : getBoqVersionRoutePrefix();
  const cv = versionHistory.currentVersion;
  const belongsToRoute = !prefix || String(cv).toUpperCase().startsWith(`${prefix}-`);
  return belongsToRoute ? getNumericBoqVersion(cv) : undefined;
}
